import { Pressable, StyleSheet, View, Text } from "react-native";
import { colors, spacing, typographyScale, webTransitions } from "../../../styles/design-system";
import React, { useState } from "react";
import { ChatIcon, DotsThreeIcon, PushPinSimpleIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { PopoverMenu } from "../../common/general/PopoverMenu/PopoverMenu";

const TITLE_MAX_WIDTH = 135;
const BORDER_RADIUS = 16;
const MIN_HEIGHT = 37;
const MENU_WIDTH = 160;

export interface ThreadCardProps {
  id: string;
  title: string;
  isActive: boolean;
  pinned?: boolean;
  onSelect?: (id: string) => void;
  onPin?: (id: string) => void;
  onRename?: (id: string) => void;
  onDelete?: (id: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
  id,
  title,
  isActive,
  pinned = false,
  onSelect,
  onPin,
  onRename,
  onDelete,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasActions = onPin || onRename || onDelete;

  const handleMenuClose = () => {
    setMenuOpen(false);
    setIsHovered(false);
  };

  const handlePin = () => {
    onPin?.(id);
    handleMenuClose();
  };

  const handleRename = () => {
    onRename?.(id);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete?.(id);
    handleMenuClose();
  };

  const showMenuButton = isHovered && hasActions;

  return (
    <View
      style={[
        styles.card,
        isActive && styles.cardActiveBackground,
        !isActive && isHovered && styles.cardHoverBackground,
      ]}
      {...(onMouseEnter && onMouseLeave ? { onMouseEnter, onMouseLeave } : {})}>
      <Pressable
        style={styles.main}
        onPress={() => onSelect?.(id)}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={title}>
        <View style={styles.iconWrap}>
          <ChatIcon size={16} color={isActive ? colors.brand[700] : colors.grey[500]} />
        </View>
        <Text style={[styles.title, isActive && styles.titleActive]} numberOfLines={1} ellipsizeMode="tail">
          {title}
        </Text>
      </Pressable>
      {hasActions && (
        <View
          style={styles.menuContainer}
          {...{
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => !menuOpen && setIsHovered(false),
          }}>
          <PopoverMenu
            placement="bottom-end"
            menuWidth={MENU_WIDTH}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            triggerAccessibilityLabel="Thread options"
            renderTrigger={() => <DotsThreeIcon size={14} color={colors.grey[600]} weight="bold" />}
            triggerStyle={(state) => {
              const hovered = (state as { hovered?: boolean }).hovered ?? false;
              return [
                styles.menuTrigger,
                { opacity: showMenuButton || menuOpen ? 1 : 0 },
                hovered && styles.menuTriggerVisible,
              ];
            }}
            items={[
              ...(onPin
                ? [
                    {
                      key: "pin",
                      label: pinned ? "Unpin" : "Pin",
                      icon: <PushPinSimpleIcon size={14} color={colors.grey[900]} />,
                      onPress: handlePin,
                    },
                  ]
                : []),
              ...(onRename
                ? [
                    {
                      key: "rename",
                      label: "Rename",
                      icon: <PencilSimpleIcon size={14} color={colors.grey[900]} />,
                      onPress: handleRename,
                    },
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      key: "delete",
                      label: "Delete",
                      icon: <TrashIcon size={14} color={colors.error[600]} />,
                      onPress: handleDelete,
                      danger: true,
                    },
                  ]
                : []),
            ]}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: MIN_HEIGHT,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: BORDER_RADIUS,
    gap: spacing.xs,
    minWidth: 0,
  },
  cardActiveBackground: {
    backgroundColor: colors.brand[50],
  },
  cardHoverBackground: {
    backgroundColor: colors.grey[50],
  },
  main: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    minWidth: 0,
  },
  iconWrap: {
    flexShrink: 0,
  },
  title: {
    flex: 1,
    maxWidth: TITLE_MAX_WIDTH,
    fontSize: typographyScale.textSm.fontSize,
    fontFamily: "Inter",
    fontWeight: "500",
    color: colors.grey[700],
    minWidth: 0,
  },
  titleActive: {
    color: colors.brand[700],
  },
  menuContainer: {
    position: "relative",
    flexShrink: 0,
  },
  menuTrigger: {
    padding: 6,
    marginRight: 4,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  menuTriggerVisible: {
    backgroundColor: colors.grey[50],
  },
});

export default ThreadCard;
